import apiClient from './apiClient';
import { DealProps } from '../types/deal';
import cachedRequest from './cachedRequest';
import { trackError } from '../utils/errorTracking';

const API_PATH = '/deals';

// Get all deals with pagination and filters
export const getDeals = async (page = 1, limit = 10, sort = '-createdAt', filters = {}) => {
  const cacheKey = `deals:${page}:${limit}:${sort}:${JSON.stringify(filters)}`;

  return cachedRequest(
    cacheKey,
    async () => {
      try {
        // Build query string from parameters
        const queryParams = new URLSearchParams({
          page: page.toString(),
          limit: limit.toString(),
          sort,
        });

        // Add any additional filters (only meaningful values)
        Object.entries(filters).forEach(([key, value]) => {
          // Special handling for freeShipping: only include when true
          if (key === 'freeShipping') {
            if (value === true) {
              queryParams.append(key, 'true');
            }
            // Skip when false - this allows all items to be shown
          } else if (value !== null && value !== undefined && value !== '') {
            queryParams.append(key, value as string);
          }
        });

        const response = await apiClient.get(`${API_PATH}?${queryParams}`);

        return {
          deals: response.data.data,
          pagination: response.data.pagination,
          total: response.data.count,
        };
      } catch (error) {
        console.error('Error fetching deals:', error);
        trackError(error as Error, 'dealService:getDeals');
        throw error;
      }
    },
    {
      cacheTTL: 30 * 1000, // Cache for 30 seconds
      dedupeTTL: 5 * 1000, // Deduplicate for 5 seconds
    }
  );
};

// Get trending deals with pagination and filters (sorted by dealScore)
export const getTrendingDeals = async (page = 1, limit = 10, sort = '-dealScore', filters = {}) => {
  const cacheKey = `trending:${page}:${limit}:${sort}:${JSON.stringify(filters)}`;

  return cachedRequest(
    cacheKey,
    async () => {
      try {
        // Build query string from parameters
        const queryParams = new URLSearchParams({
          page: page.toString(),
          limit: limit.toString(),
          sort,
        });

        // Add any additional filters (only meaningful values)
        Object.entries(filters).forEach(([key, value]) => {
          // Special handling for freeShipping: only include when true
          if (key === 'freeShipping') {
            if (value === true) {
              queryParams.append(key, 'true');
            }
            // Skip when false - this allows all items to be shown
          } else if (value !== null && value !== undefined && value !== '') {
            queryParams.append(key, value as string);
          }
        });

        const response = await apiClient.get(`${API_PATH}/trending?${queryParams}`);

        return {
          deals: response.data.data,
          pagination: response.data.pagination,
          total: response.data.count,
        };
      } catch (error) {
        console.error('Error fetching trending deals:', error);
        throw error;
      }
    },
    {
      cacheTTL: 60 * 1000, // Cache trending deals for 60 seconds (they change less frequently)
      dedupeTTL: 5 * 1000,
    }
  );
};

// Get a single deal by ID
export const getDealById = async (id: string) => {
  try {
    const response = await apiClient.get(`${API_PATH}/${id}`);
    return response.data.data;
  } catch (error) {
    console.error(`Error fetching deal with ID ${id}:`, error);
    throw error;
  }
};

// Get deal with all related data (aggregated endpoint)
export const getDealFull = async (id: string) => {
  try {
    const response = await apiClient.get(`${API_PATH}/${id}/full`);
    return response.data.data;
  } catch (error) {
    console.error(`Error fetching full deal data with ID ${id}:`, error);
    trackError(error as Error, 'dealService:getDealFull');
    throw error;
  }
};

// Create a new deal
export const createDeal = async (dealData: Partial<DealProps> | FormData) => {
  try {
    const headers =
      dealData instanceof FormData
        ? { 'Content-Type': 'multipart/form-data' }
        : { 'Content-Type': 'application/json' };

    const response = await apiClient.post(API_PATH, dealData, { headers });
    return response.data.data;
  } catch (error) {
    console.error('Error creating deal:', error);
    throw error;
  }
};

// Update a deal
export const updateDeal = async (id: string, dealData: Partial<DealProps> | FormData) => {
  try {
    const headers =
      dealData instanceof FormData
        ? { 'Content-Type': 'multipart/form-data' }
        : { 'Content-Type': 'application/json' };

    const response = await apiClient.put(`${API_PATH}/${id}`, dealData, { headers });
    return response.data.data;
  } catch (error) {
    console.error(`Error updating deal with ID ${id}:`, error);
    throw error;
  }
};

// Delete a deal
export const deleteDeal = async (id: string) => {
  try {
    await apiClient.delete(`${API_PATH}/${id}`);
    return true;
  } catch (error) {
    console.error(`Error deleting deal with ID ${id}:`, error);
    throw error;
  }
};

// Vote on a deal (upvote/downvote)
export const voteDeal = async (id: string, voteValue: 1 | -1) => {
  try {
    const response = await apiClient.post(`${API_PATH}/${id}/vote`, { voteValue });
    return response.data.data;
  } catch (error: any) {
    throw error;
  }
};

// Save a deal to user's saved deals
export const saveDeal = async (id: string) => {
  try {
    const response = await apiClient.post(`${API_PATH}/${id}/save`);
    return response.data.data;
  } catch (error) {
    console.error(`Error saving deal with ID ${id}:`, error);
    throw error;
  }
};

// Remove a deal from user's saved deals
export const unsaveDeal = async (id: string) => {
  try {
    const response = await apiClient.delete(`${API_PATH}/${id}/save`);
    return response.data.data;
  } catch (error) {
    console.error(`Error unsaving deal with ID ${id}:`, error);
    throw error;
  }
};

// Get user's saved deals with pagination
export const getSavedDeals = async (userId?: string, page = 1, limit = 10) => {
  try {
    // Build query string for pagination
    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    // If userId is provided, get saved deals for that user (admin functionality)
    const url = userId
      ? `/users/${userId}/saved-deals?${queryParams}`
      : `/users/me/saved-deals?${queryParams}`;

    const response = await apiClient.get(url);

    return {
      deals: response.data.data,
      pagination: response.data.pagination,
      total: response.data.count,
    };
  } catch (error) {
    console.error('Error fetching saved deals:', error);
    throw error;
  }
};

// Check if a deal is saved by the current user
export const checkDealSaved = async (id: string) => {
  try {
    const response = await apiClient.get(`${API_PATH}/${id}/saved`);
    return response.data.saved;
  } catch (error) {
    console.error(`Error checking if deal with ID ${id} is saved:`, error);
    return false; // Assume not saved if there's an error
  }
};

// Check user's vote status for a deal
export const checkDealVoteStatus = async (id: string) => {
  try {
    const response = await apiClient.get(`${API_PATH}/${id}/vote-status`);
    return response.data.data.voteValue || 0; // Return 1, -1, or 0
  } catch (error) {
    console.error(`Error checking vote status for deal with ID ${id}:`, error);
    return 0; // Assume no vote if there's an error
  }
};

// Approve a deal (admin only)
export const approveDeal = async (id: string) => {
  try {
    const response = await apiClient.put(`${API_PATH}/${id}/approve`, {});
    return response.data.data;
  } catch (error) {
    console.error(`Error approving deal with ID ${id}:`, error);
    throw error;
  }
};

// Reject a deal (admin only)
export const rejectDeal = async (id: string) => {
  try {
    const response = await apiClient.put(`${API_PATH}/${id}/reject`, {});
    return response.data.data;
  } catch (error) {
    console.error(`Error rejecting deal with ID ${id}:`, error);
    throw error;
  }
};

// Get deals by niche
export const getDealsByNiche = async (
  slug: string,
  page = 1,
  limit = 10,
  sort = '-dealScore',
  filters = {}
) => {
  try {
    // Build query string from parameters
    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      sort,
    });

    // Add any additional filters
    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        queryParams.append(key, value as string);
      }
    });

    const response = await apiClient.get(`${API_PATH}/niche/${slug}?${queryParams}`);

    return {
      deals: response.data.data,
      pagination: response.data.pagination,
      total: response.data.count,
      nicheName: response.data.niche,
    };
  } catch (error) {
    console.error('Error fetching deals by niche:', error);
    throw error;
  }
};

// Get deals by category slug
export const getDealsByCategory = async (
  categorySlug: string,
  page = 1,
  limit = 10,
  sort = '-dealScore',
  filters = {}
) => {
  try {
    // Build query string from parameters
    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      sort,
    });

    // Add any additional filters
    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        queryParams.append(key, value as string);
      }
    });

    const response = await apiClient.get(`${API_PATH}/category/${categorySlug}?${queryParams}`);

    return {
      deals: response.data.data,
      pagination: response.data.pagination,
      total: response.data.count,
    };
  } catch (error) {
    console.error('Error fetching deals by category:', error);
    throw error;
  }
};

// Bulk check both saved and vote status for multiple deals in one call
export const checkBulkDealStatus = async (
  dealIds: string[]
): Promise<{
  savedStatus: Record<string, boolean>;
  voteStatus: Record<string, number>;
}> => {
  // Sort deal IDs for consistent cache keys
  const sortedIds = [...dealIds].sort();
  const cacheKey = `bulk-status:${sortedIds.join(',')}`;

  return cachedRequest(
    cacheKey,
    async () => {
      try {
        const response = await apiClient.post('/deals/bulk/status', { dealIds });
        return {
          savedStatus: response.data.data.savedStatus,
          voteStatus: response.data.data.voteStatus,
        };
      } catch (error) {
        throw error;
      }
    },
    {
      cacheTTL: 30 * 1000, // Cache bulk status for 30 seconds
      dedupeTTL: 2 * 1000, // Short deduplication window for bulk requests
    }
  );
};

// Bulk check saved status for multiple deals
export const checkBulkDealSaved = async (dealIds: string[]): Promise<Record<string, boolean>> => {
  try {
    const response = await apiClient.post('/deals/bulk/saved-status', { dealIds });
    return response.data.data.savedStatus;
  } catch (error) {
    throw error;
  }
};

// Bulk check vote status for multiple deals
export const checkBulkDealVoteStatus = async (
  dealIds: string[]
): Promise<Record<string, number>> => {
  try {
    const response = await apiClient.post('/deals/bulk/vote-status', { dealIds });
    return response.data.data.voteStatus;
  } catch (error) {
    throw error;
  }
};

// Search deals with text search and filters
export const searchDeals = async (
  query: string,
  page = 1,
  limit = 10,
  sort = 'relevance',
  filters = {}
) => {
  try {
    // Build query string from parameters
    const queryParams = new URLSearchParams({
      q: query,
      page: page.toString(),
      limit: limit.toString(),
      sort,
    });

    // Add any additional filters
    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        queryParams.append(key, value as string);
      }
    });

    const response = await apiClient.get(`${API_PATH}/search?${queryParams}`);

    return {
      deals: response.data.data,
      pagination: response.data.pagination,
      total: response.data.count,
      searchQuery: response.data.searchQuery,
    };
  } catch (error) {
    console.error('Error searching deals:', error);
    throw error;
  }
};

// Get user's submitted deals
export const getUserSubmittedDeals = async (
  userId: string,
  page: number = 1,
  limit: number = 10
) => {
  try {
    const response = await apiClient.get(
      `/deals/user/${userId}/submitted?page=${page}&limit=${limit}`
    );
    return {
      deals: response.data.data,
      pagination: response.data.pagination,
    };
  } catch (error: any) {
    console.error('Error fetching user submitted deals:', error);
    throw error;
  }
};
